import { Link } from "react-router-dom";
import { SERVICES } from "../../../mocks/businessData";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const FEATURED = SERVICES.slice(0, 2);
const REMAINING = SERVICES.slice(2);

const FEATURED_IMGS = [
  "https://public.readdy.ai/ai/img_res/8acb357001139a908e9e51234de57aa4.jpg",
  "https://readdy.ai/api/search-image?query=elegant%20wood%20venetian%20blinds%20and%20sophisticated%20designer%20roller%20shades%20in%20a%20modern%20luxury%20home%20office%2C%20warm%20minimal%20interior%20design%20with%20clean%20lines%2C%20neutral%20stone%20and%20cream%20palette%2C%20natural%20light%20casting%20beautiful%20shadow%20patterns%20through%20the%20slats%2C%20perfectly%20cleaned%20and%20dusted%20blinds%2C%20premium%20window%20treatment%20photography%2C%20high-end%20residential%20interior%20setting%2C%20editorial%20quality%20image&width=900&height=700&seq=svc_blinds_feat_v5&orientation=landscape",
];

const BRANDS = [
  "Hunter Douglas", "Graber", "Levolor", "Blinds To Go",
  "Home Depot", "Ethan Allen", "Lowe&apos;s", "JC Penney",
];

export default function ServicesOverview() {
  const ref = useScrollReveal();

  return (
    <section className="bg-white" ref={ref}>
      {/* ── Section Header ── */}
      <div className="py-20 max-w-7xl mx-auto px-6 text-center">
        <div className="reveal">
          <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-4">
            Artisan Restoration
          </p>
          <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl text-stone-900 mb-5 leading-[1.05]">
            Every Treatment,{" "}
            <span className="italic text-amber-700">Renewed.</span>
          </h2>
          <div className="gold-rule mx-auto mb-7" />
          <p className="text-stone-500 text-base max-w-xl mx-auto leading-relaxed">
            From heirloom silk draperies to precision-engineered Hunter Douglas silhouettes —
            every piece is professionally restored at our facility and rehung to absolute perfection.
          </p>
        </div>
      </div>

      {/* ── Alternating Featured Rows ── */}
      {FEATURED.map((service, i) => (
        <div
          key={service.id}
          className={`relative flex flex-col ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"}`}
        >
          {/* Image */}
          <div className="lg:w-1/2 relative overflow-hidden h-72 md:h-[480px] lg:h-[560px] w-full">
            <img
              src={FEATURED_IMGS[i]}
              alt={`${service.title} cleaning service`}
              className="w-full h-full object-cover object-top hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
            />
            {/* Overlay */}
            <div className={`absolute inset-0 bg-gradient-to-${i % 2 === 0 ? "r" : "l"} from-transparent via-transparent to-stone-950/15`} />
            {/* Big number watermark */}
            <div className="absolute bottom-5 right-6 font-serif text-8xl text-white/10 leading-none select-none pointer-events-none">
              0{i + 1}
            </div>
            {/* Floating service tag */}
            <div className="absolute top-6 left-6">
              <span className="bg-amber-700 text-white text-xs tracking-[0.25em] uppercase font-semibold px-3 py-1.5">
                {service.title}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-1/2 bg-stone-50 flex items-center">
            <div className={`px-10 md:px-16 lg:px-20 py-16 reveal reveal-delay-${i + 1} max-w-lg ${i % 2 !== 0 ? "ml-auto" : ""}`}>
              <p className="text-amber-700/70 text-xs tracking-[0.35em] uppercase font-semibold mb-5">
                Service 0{i + 1} of {SERVICES.length}
              </p>
              <h3 className="font-serif font-light text-4xl md:text-5xl text-stone-900 mb-4 leading-tight">
                {service.title} Cleaning
              </h3>
              <div className="gold-rule mb-7" />
              <p className="text-stone-600 text-base leading-relaxed mb-4">
                {service.description}
              </p>
              <p className="text-stone-400 text-sm leading-relaxed mb-9">
                {service.fullDescription}
              </p>
              <div className="flex items-center gap-5">
                <a
                  href="tel:19734891019"
                  className="btn-luxury bg-stone-900 hover:bg-amber-700 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-3.5 rounded-sm whitespace-nowrap cursor-pointer transition-colors duration-300"
                >
                  Get a Free Quote
                </a>
                <Link
                  to="/services"
                  className="flex items-center gap-1.5 text-stone-400 hover:text-amber-700 text-xs tracking-widest uppercase font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  All Details
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* ── Remaining 4 Services — Depth Grid ── */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REMAINING.map((service, i) => (
              <div
                key={service.id}
                className={`reveal reveal-delay-${i + 1} service-card-hover depth-card group bg-stone-50 border border-stone-100 hover:border-amber-100 overflow-hidden cursor-pointer`}
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52 w-full">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.06] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/55 via-transparent" />
                  {/* Number */}
                  <span className="absolute bottom-3 right-4 font-serif text-4xl text-white/15 select-none leading-none">
                    0{i + 3}
                  </span>
                  {/* Hover CTA */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="bg-amber-700 text-white text-xs tracking-[0.2em] uppercase font-semibold px-4 py-2">
                      Learn More
                    </span>
                  </div>
                </div>
                {/* Text */}
                <div className="p-5">
                  <h3 className="font-serif text-2xl text-stone-900 mb-2 group-hover:text-amber-800 transition-colors duration-300">
                    {service.title} Cleaning
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed mb-4 line-clamp-3">
                    {service.description}
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-900 text-xs tracking-widest uppercase font-semibold transition-colors cursor-pointer"
                  >
                    Details <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="reveal mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/services"
              className="btn-luxury inline-flex items-center gap-2 border-2 border-stone-200 hover:border-amber-700 text-stone-700 hover:text-amber-700 text-xs tracking-[0.22em] uppercase font-semibold px-10 py-4 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-fill" />
              View All 6 Services in Detail
            </Link>
            <a
              href="tel:19734891019"
              className="btn-luxury inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-10 py-4 rounded-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              Request a Free Estimate
            </a>
          </div>
        </div>
      </div>

      {/* ── Brands We Service Band ── */}
      <div className="bg-stone-950 border-t border-stone-800 py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-stone-500 text-xs tracking-[0.35em] uppercase font-semibold mb-8">
            Brands We Expertly Service
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {BRANDS.map((brand, i) => (
              <span
                key={i}
                className="font-serif text-stone-400 hover:text-amber-400 text-lg tracking-wide transition-colors duration-300 cursor-default whitespace-nowrap"
                dangerouslySetInnerHTML={{ __html: brand }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
